import { SectionCard } from "@/features/power_projects/components/ui";
import type { EstimateJobDetail } from "../types";

/**
 * Lista kontrolna kosztorysu wygenerowanego z rysunku.
 *
 * Sedno „żeby o niczym nie zapomnieć": pokazuje, czego reguły NIE pokryły. Cichy brak
 * byłby gorszy niż brak generatora — dlatego karta stoi obok kwot, a nie zamiast nich,
 * i nie znika, gdy jest pusta: „wszystko pokryte" to też odpowiedź, i trzeba ją widzieć.
 */
export function TakeoffReportCard({ job }: { job: EstimateJobDetail }) {
  if (job.source !== "takeoff") return null;
  const report = job.takeoff_report ?? {};
  const uncovered = report.uncovered ?? [];
  const checks = report.checks ?? [];
  const errors = report.errors ?? [];
  const clean = !uncovered.length && !checks.length && !errors.length;

  return (
    <SectionCard title="Lista kontrolna obmiaru">
      {clean ? (
        <p className="text-[12px] text-emerald-700">
          ✓ Każdy narysowany element trafił do przedmiaru, a reguły kontrolne milczą.
        </p>
      ) : (
        <div className="space-y-3">
          {errors.length > 0 && (
            <div>
              <p className="section-label mb-1">Reguły, które się nie policzyły</p>
              <ul className="space-y-0.5">
                {errors.map((e, i) => (
                  <li key={i} className="text-[12px] text-[var(--color-primary)]">
                    ✗ {e}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[10px] text-[var(--color-text-tertiary)]">
                Te pozycje NIE powstały — popraw wzór w regułach obmiaru i wygeneruj
                jeszcze raz.
              </p>
            </div>
          )}
          {uncovered.length > 0 && (
            <div>
              <p className="section-label mb-1">Narysowane, ale poza przedmiarem</p>
              <ul className="space-y-0.5">
                {uncovered.map((row) => (
                  <li
                    key={row.element_type}
                    className="flex items-baseline justify-between gap-2 text-[12px]"
                  >
                    <span className="text-[var(--color-text-secondary)]">
                      {row.label}{" "}
                      <span className="text-[var(--color-text-tertiary)]">
                        ({row.features} szt. na rysunku)
                      </span>
                    </span>
                    <span className="shrink-0 text-[10px] text-amber-600">
                      {row.has_rules
                        ? "reguły są, żadna nie pasuje do parametrów"
                        : "brak jakiejkolwiek reguły"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {checks.length > 0 && (
            <div>
              <p className="section-label mb-1">Przypomnienia</p>
              <ul className="space-y-0.5">
                {checks.map((c, i) => (
                  <li key={i} className="text-[12px] text-amber-700">
                    ⚠ {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
